import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { usePermissions, useRole } from '@gm-commercial/profile-context';
import { Role } from '@gm-commercial/profile-model';
import type { InviteDto } from '@gm-commercial/rtk-clients/users/types';
import { Alert } from '@mui/material';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  generatePath,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router-dom';
import { createSelector } from 'reselect';
import type { RootState } from '~/app/rootReducer';
import TranslatedLabel from '~/common/components/TranslatedLabel/TranslatedLabel';
import type { RouteParams } from '~/common/configs/route.config';
import useSearchParams from '~/common/hooks/useSearchParams';
import { BDRequestStatus } from '~/common/models/apis/apiResponse.model';
import { ACCOUNT_PROFILE_PERSONAL_PATH } from '~/common/models/pages/accountPages.model';
import { type RouteConfig, RouteName } from '~/common/models/route.model';
import { canViewMigrationNotCompleteWithoutProfile } from '~/common/utils/enrollment.utils';
import {
  findRoute,
  getAdvisorHomePath,
  getImpersonationHomePath,
} from '~/common/utils/route/route.utils';
import {
  ProfileRequestType,
  selectProfileState,
} from '~/store/profile/profileSlice';
import {
  selectActingProfile,
  selectActingProfileContextIds,
  selectActingProfileId,
  selectActingProfileInvite,
  selectActingProfileStatus,
  selectProfile,
  selectProfileContextIds,
  selectProfileId,
  selectProfileInvite,
  selectProfileStatus,
} from '~/store/profile/profileSlice.selectors';

type PrivateRouteProps = Pick<
  RouteConfig,
  | 'authorizedPerms'
  | 'authorizedRoles'
  | 'excludedRoles'
  | 'path'
  | 'requiredFeatureFlags'
  | 'anyFeatureFlags'
  | 'name'
  | 'element'
  | 'requiresContextMatch'
  | 'customTemplate'
> & {
  actingProfile?: boolean;
};

const selectProfileRequestStatus = createSelector(
  (state: RootState) =>
    selectProfileState(state).operations[ProfileRequestType.GET_PROFILE],
  (getProfileRequest) => getProfileRequest?.status
);

const selectProfileParams = createSelector(
  selectProfileId,
  selectProfileStatus,
  selectProfileContextIds,
  selectProfileInvite,
  (id, status, contextIds, invite) => {
    return { id, status, invite, ...contextIds };
  }
);

const selectActingProfileParams = createSelector(
  selectActingProfileId,
  selectActingProfileStatus,
  selectActingProfileContextIds,
  selectActingProfileInvite,
  (id, status, contextIds, invite) => {
    return { id, status, invite, ...contextIds };
  }
);

/**
 * Route config path for the onboarding surface that matches a pending user's
 * invite activation state. Use with `generatePath` when navigating.
 */
const getPendingOnboardingRoutePath = (
  activationState: InviteDto['activationState'] | undefined
): string => {
  switch (activationState) {
    case 'AWAITING_ACCOUNT_ADMIN':
      return findRoute(RouteName.TERMS_NOT_SIGNED).path;
    case 'MIGRATION_IN_PROGRESS':
      return findRoute(RouteName.MIGRATION_NOT_COMPLETE).path;
    default:
      return findRoute(RouteName.USER_TERMS_AND_CONDITIONS).path;
  }
};

const SecureRoute = ({
  actingProfile = true,
  ...props
}: PrivateRouteProps): JSX.Element => {
  const {
    authorizedPerms,
    authorizedRoles,
    excludedRoles,
    path,
    requiredFeatureFlags,
    anyFeatureFlags,
  } = props;
  const { hasPermission } = usePermissions();
  const baseProfile = useSelector(selectProfile);
  const isGmfAdvisor = baseProfile?.role === Role.GMF_ADVISOR;
  const { isRoleAuthorized } = useRole(actingProfile && !isGmfAdvisor);
  const { getFlag } = useFeatureFlags();
  const location = useLocation();
  const params = useParams<RouteParams>();

  const searchParams = useSearchParams();

  const isAuthorized = useMemo(
    () =>
      (isGmfAdvisor && path === ACCOUNT_PROFILE_PERSONAL_PATH) ||
      hasPermission(authorizedPerms || []),
    [hasPermission, authorizedPerms, isGmfAdvisor, path]
  );
  const roleAllowed = useMemo(
    () => isRoleAuthorized({ authorizedRoles, excludedRoles }),
    [isRoleAuthorized, authorizedRoles, excludedRoles]
  );
  const isFeatureFlagEnabled = useMemo(() => {
    return !requiredFeatureFlags?.some((flag) => !getFlag(flag));
  }, [requiredFeatureFlags, getFlag]);
  // OR semantics, separate from requiredFeatureFlags' AND: enabled when any
  // listed flag is on. Matches route.utils.ts's handling of this same field.
  const isAnyFeatureFlagEnabled = useMemo(() => {
    if (!anyFeatureFlags?.length) return true;
    return anyFeatureFlags.some((flag) => !!getFlag(flag));
  }, [anyFeatureFlags, getFlag]);

  const profileParams = useSelector(
    actingProfile && !isGmfAdvisor
      ? selectActingProfileParams
      : selectProfileParams
  );
  const profileRequestStatus = useSelector(selectProfileRequestStatus);

  const currentActingProfile = useSelector(selectActingProfile);
  const impersonationHomePath = useMemo(
    () => getImpersonationHomePath(baseProfile, currentActingProfile),
    [baseProfile, currentActingProfile]
  );
  const advisorHomePath = useMemo(
    () => getAdvisorHomePath(baseProfile, currentActingProfile),
    [baseProfile, currentActingProfile]
  );

  // Get context IDs from either search params or route params
  const organizationsId =
    searchParams?.organizationsId || params?.organizationsId;
  const hubsId = searchParams?.hubsId || params?.hubsId;
  const fleetsId = searchParams?.fleetsId || params?.fleetsId;

  const hasValidContext = useMemo(() => {
    const contextParams = { organizationsId, hubsId, fleetsId } as const;

    // for internal / advisor users there will not be any context params, skip validation.
    const hasAnyContextParam = Object.values(contextParams).some(Boolean);
    if (!hasAnyContextParam) return true;

    // Check if each provided context param matches the profile's context
    for (const [key, paramValue] of Object.entries(contextParams)) {
      if (!paramValue) continue;
      if (
        profileParams[key as keyof typeof profileParams] &&
        profileParams[key as keyof typeof profileParams] !== paramValue
      ) {
        return false;
      }
    }
    return true;
  }, [organizationsId, hubsId, fleetsId, profileParams]);

  // Pending users may only remain on the onboarding surface that matches their
  // invite activation state; any other secure path is redirected there.
  if (profileParams.status === 'pending') {
    const pendingRoutePath = getPendingOnboardingRoutePath(
      profileParams.invite?.activationState
    );
    if (path === pendingRoutePath) {
      return <Outlet />;
    }
    return (
      <Navigate
        to={generatePath(pendingRoutePath, { usersId: profileParams.id })}
      />
    );
  }

  if (canViewMigrationNotCompleteWithoutProfile(props.name, profileParams.id)) {
    return <Outlet />;
  }
  // Early return for unauthenticated or unauthorized users
  const meetsNonRoleRequirements =
    profileParams.status === 'active' &&
    isAuthorized &&
    hasValidContext &&
    isFeatureFlagEnabled &&
    isAnyFeatureFlagEnabled;

  const isFullyAuthorized = meetsNonRoleRequirements && roleAllowed;

  if (!isFullyAuthorized) {
    if (
      !profileRequestStatus ||
      profileRequestStatus === BDRequestStatus.IDLE
    ) {
      return (
        <Navigate
          to="/"
          // preserve location from the initial URL load that will be used in InboundRedirect
          state={{ SecureRoute: location }}
        />
      );
    }

    // Inside this branch `meetsNonRoleRequirements` means the acting role is the
    // only thing denying the route, which is an advisor-scoped page reached mid
    // impersonation: send them to the impersonated org's home instead, since
    // only logout or "Exit organization" ends impersonation. A missing
    // permission or feature flag still earns the denial screen. The path
    // comparison keeps a denial on that home page from looping.
    if (
      impersonationHomePath &&
      meetsNonRoleRequirements &&
      impersonationHomePath !== location.pathname
    ) {
      return <Navigate to={impersonationHomePath} replace />;
    }

    // "Exit organization" clears impersonation while still on the org page, so
    // a route the advisor cannot see must fall back to the org list.
    if (
      !isGmfAdvisor &&
      advisorHomePath &&
      advisorHomePath !== location.pathname
    ) {
      return <Navigate to={advisorHomePath} replace />;
    }

    if (isGmfAdvisor) {
      return (
        <Alert severity="error">
          <TranslatedLabel translationKey="common:alertMsg.messageView.unauthorized.header" />
        </Alert>
      );
    }
    return <Navigate to="/unauthorized" state={{ SecureRoute: location }} />;
  }

  return <Outlet />;
};

export default SecureRoute;
